import { useState, useEffect } from "react";
import { getApiUrl, getBaseUrl } from "@/config/appConfig";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Plus, Layout, Key, Trash2, Edit, Eye, Link as LinkIcon, Unlink, Calendar, Clock, Copy, Tag } from "lucide-react";

// Using centralized config
const getAPI = () => getApiUrl();
const API = `${getAPI()}`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_layouts: 0, linked_to_events: 0 });
  const [seatStats, setSeatStats] = useState({}); // { layoutId: { available, held, booked } }

  useEffect(() => {
    fetchLayouts();
    fetchStats();
  }, []);
const fetchLayouts = async () => {
    try {
      const response = await axios.get(`${API}/admin/layouts`);
      setLayouts(response.data);
      
      // Fetch seat stats for each layout
      for (const layout of response.data) {
        fetchSeatStats(layout.id);
      }
    } catch (error) {
      toast.error("Failed to fetch layouts");
    } finally {
      setLoading(false);
    }
  };

  const fetchSeatStats = async (layoutId) => {
    try {
      const response = await axios.get(`${API}/admin/layouts/${layoutId}/seat-stats`);
      setSeatStats(prev => ({ ...prev, [layoutId]: response.data }));
    } catch (error) {
      // Default to zeros if API fails
      setSeatStats(prev => ({ ...prev, [layoutId]: { available: 0, held: 0, booked: 0 } }));
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/layouts/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats");
    }
  };

  const handleDelete = async (layoutId) => {
    if (!window.confirm("Are you sure you want to delete this layout?")) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/layouts/${layoutId}`);
      toast.success("Layout deleted successfully");
      fetchLayouts();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete layout");
    }
  };

  const handleDuplicate = async (layout) => {
    try {
      const duplicateData = {
        name: `Copy of ${layout.name}`,
        venue_name: layout.venue_name,
        canvas_objects: layout.canvas_objects || [],
        grid_snap: layout.grid_snap ?? true
      };
      
      const response = await axios.post(`${API}/admin/layouts`, duplicateData);
      toast.success(`Layout duplicated as "Copy of ${layout.name}"`);
      fetchLayouts();
      fetchStats();
    } catch (error) {
      toast.error("Failed to duplicate layout");
    }
  };

  const calculateTotalSeats = (layout) => {
    if (layout.canvas_objects && layout.canvas_objects.length > 0) {
      let total = 0;
      layout.canvas_objects.forEach(obj => {
        if (obj.type === 'seat') total += 1;
        else if (obj.type === 'single_row') total += obj.properties?.seat_count || 0;
        else if (obj.type === 'multiple_rows') {
          const rows = obj.properties?.row_count || 0;
          const seatsPerRow = obj.properties?.seat_count || 0;
          total += rows * seatsPerRow;
        }
        else if (obj.type === 'round_table' || obj.type === 'rect_table') total += obj.properties?.seats || 0;
        else if (obj.type === 'ga_zone' || obj.type === 'vip_pod' || obj.type === 'grouped_block') total += obj.properties?.capacity || 0;
        else if (obj.type === 'sofa') total += obj.properties?.seater_count || 3;
      });
      return total;
    } else if (layout.rows && layout.seats_per_row) {
      return layout.rows.length * layout.seats_per_row;
    }
    return 0;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50/50">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                SeatPilot
              </h1>
              <p className="text-slate-600 mt-1" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                Smart Seating Maps for Events
              </p>
              <p className="text-slate-400 text-xs mt-0.5" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                Powered by TicketKart
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/api-keys')}
                variant="outline"
                className="border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300"
                data-testid="api-keys-button"
              >
                <Key className="w-4 h-4 mr-2" />
                API Keys
              </Button>
              <Button
                onClick={() => navigate('/builder')}
                className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
                data-testid="create-layout-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Layout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-white rounded-lg border border-slate-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Layouts</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {stats.total_layouts}
                </p>
              </div>
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Layout className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </Card>
          <Card className="bg-white rounded-lg border border-slate-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Linked to Events</p>
                <p className="text-3xl font-bold text-green-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {stats.linked_to_events}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <LinkIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </Card>
          <Card className="bg-white rounded-lg border border-slate-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Available</p>
                <p className="text-3xl font-bold text-slate-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {stats.total_layouts - stats.linked_to_events}
                </p>
              </div>
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-slate-600" />
              </div>
            </div>
          </Card>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-slate-500" style={{ fontFamily: 'Public Sans, sans-serif' }}>Loading layouts...</div>
          </div>
        ) : layouts.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-state">
            <Layout className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              No layouts yet
            </h2>
            <p className="text-slate-500 mb-6" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              Create your first seating layout to get started
            </p>
            <Button
              onClick={() => navigate('/builder')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Layout
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="layouts-grid">
            {layouts.map((layout) => {
              const totalSeats = calculateTotalSeats(layout);
              const layoutSeatStats = seatStats[layout.id] || { available: totalSeats, held: 0, booked: 0 };
              const isLinked = !!layout.event_id;
              
              return (
                <Card
                  key={layout.id}
                  className="bg-white rounded-lg border border-slate-200 p-6 hover:border-indigo-200 transition-colors"
                  data-testid={`layout-card-${layout.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                        {layout.name}
                      </h3>
                      {layout.venue_name && (
                        <p className="text-sm text-slate-500" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                          {layout.venue_name}
                        </p>
                      )}
                      {/* Auto-generated tags */}
                      {layout.tags && layout.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {layout.tags.map(tag => (
                            <span 
                              key={tag} 
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700"
                            >
                              <Tag className="w-2.5 h-2.5" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Event link status indicator */}
                    {isLinked && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <LinkIcon className="w-3 h-3" />
                        Linked
                      </div>
                    )}
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="bg-indigo-50 rounded-lg p-3">
                      <div className="text-xs text-indigo-600 font-medium mb-1">Total Capacity</div>
                      <div className="text-2xl font-bold text-indigo-700" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {totalSeats} seats
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="bg-green-50 rounded p-2 text-center">
                        <div className="text-green-600 font-semibold">Available</div>
                        <div className="text-green-700 font-mono">{layoutSeatStats.available}</div>
                      </div>
                      <div className="bg-orange-50 rounded p-2 text-center">
                        <div className="text-orange-600 font-semibold">Held</div>
                        <div className="text-orange-700 font-mono">{layoutSeatStats.held}</div>
                      </div>
                      <div className="bg-slate-100 rounded p-2 text-center">
                        <div className="text-slate-600 font-semibold">Booked</div>
                        <div className="text-slate-700 font-mono">{layoutSeatStats.booked}</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm pt-2 border-t">
                      <span className="text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>Objects:</span>
                      <span className="font-medium text-slate-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {layout.canvas_objects?.length || 0}
                      </span>
                    </div>

                    {/* Event info if linked */}
                    {isLinked && (
                      <div className="flex items-center justify-between text-sm pt-2 border-t">
                        <span className="text-slate-600">Event:</span>
                        <span className="font-medium text-green-700 text-xs">{layout.event_name || layout.event_id}</span>
                      </div>
                    )}

                    {/* Last Updated */}
                    <div className="flex items-center gap-2 text-xs text-slate-500 pt-2 border-t">
                      <Clock className="w-3 h-3" />
                      <span>Last updated: {formatDate(layout.updated_at)}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => navigate(`/builder/${layout.id}`)}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-slate-200 text-slate-700 hover:bg-slate-50"
                      data-testid={`edit-layout-${layout.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDuplicate(layout)}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-slate-200 text-slate-700 hover:bg-slate-50"
                      data-testid={`duplicate-layout-${layout.id}`}
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Duplicate
                    </Button>
                    <Button
                      onClick={() => isLinked 
                        ? toast.info(`Linked to: ${layout.event_name || layout.event_id}`) 
                        : toast.info('Link to Event feature - use API to link')
                      }
                      variant="outline"
                      size="sm"
                      className={`flex-1 ${isLinked 
                        ? 'border-green-300 text-green-700 bg-green-50 hover:bg-green-100' 
                        : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {isLinked ? (
                        <>
                          <LinkIcon className="w-4 h-4 mr-1" />
                          Linked
                        </>
                      ) : (
                        <>
                          <Unlink className="w-4 h-4 mr-1" />
                          Link
                        </>
                      )}
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      onClick={() => window.open(`/widget/${layout.id}`, '_blank')}
                      size="sm"
                      variant="outline"
                      className="w-10 h-10 p-0 border-slate-200 text-indigo-600 hover:bg-indigo-50"
                      data-testid={`view-widget-${layout.id}`}
                      title="Preview as checkout widget"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(layout.id)}
                      variant="outline"
                      size="sm"
                      className="w-10 h-10 p-0 border-red-200 text-red-600 hover:bg-red-50"
                      data-testid={`delete-layout-${layout.id}`}
                      title="Delete layout"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>
      
      {/* Footer with Legal */}
      <footer className="border-t border-slate-200 bg-white mt-12">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-700">SeatPilot</span>
              <span className="text-slate-300">·</span>
              <span className="text-xs text-slate-500">Powered by TicketKart</span>
            </div>
            <p className="text-xs text-slate-400">SeatPilot is a TicketKart product.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
